import { useEffect, useRef, useState } from 'react';

import {
  Avatar,
  ListItemAvatar,
  ListItemText,
  List,
  ListItem,
  Box,
  Badge,
  Typography,
  Grid,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AlternateEmailIcon from '@material-ui/icons/AlternateEmail';
import { Skeleton } from '@material-ui/lab';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';

import CardContextMenu from 'components/CardContextMenu';
import CardItem from 'components/CardItem';
import FilterListaAtendimentos from 'components/FilterListaAtendimentos';
import SearchBar from 'components/SearchBar';
import VirtualizedListContainer from 'components/VirtualizedListContainer';
import * as socketEvents from 'constants/socketEvents';
import useCardContextMenu from 'hooks/useCardContextMenu';
import socket from 'services/socket';
import { useAppDispatch, useAppSelector } from 'store';
import {
  GrupoInterface,
  gruposSelector,
  fetchGrupos,
  resetGrupos,
  filterGrupos,
  addNovoGrupo,
  atualizarStatusGrupo,
  atualizarFotoGrupo,
  GrupoStatusType,
  reorderGrupos,
  setTotalMensagensNaoLidasGrupo,
  setCanalMencionadoGrupo,
  atualizarUltimaMensagemRecebidaGrupo,
  fetchGrupoById,
  setGrupoSelecionado,
  fetchMarcarGrupoComoLido,
  fetchMarcarGrupoComoNaoLido,
  setMarcarGrupoComoLido,
  setMarcarGrupoComoNaoLido,
  fetchMarcarTodosGruposComoLidos,
  setMarcarTodosGruposComoLidos,
} from 'store/reducers/grupos';
import {
  ContarNovaMensagemGrupoInterface,
  mensagensSelector,
} from 'store/reducers/mensagens';

const useStyles = makeStyles(({ spacing }) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  list: {
    height: '100%',
    overflowY: 'auto',
    paddingRight: spacing(1),
    margin: spacing(1, 0),
  },
  cardItem: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  badge: {
    position: 'relative',
    transform: 'scale(1) translate(0, 0)',
  },
}));

interface GrupoListItemProps {
  resetSelectedFiles: () => void;
}

export default function ListaGrupos({
  resetSelectedFiles,
}: GrupoListItemProps) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirm();
  const [filter, setFilter] = useState('');
  const [selectedGrupoId, setSelectedGrupoId] = useState<string | undefined>();

  const dispatch = useAppDispatch();
  const {
    grupos,
    filteredGrupos,
    loadingGrupos,
    grupoSelecionado,
    loadingGrupoSelecionado,
  } = useAppSelector(gruposSelector);
  const { loadingMensagensGrupo } = useAppSelector(mensagensSelector);

  // mantém o estado local sincronizado com o Redux (ex: reset por evento de socket)
  // Evita resetar o ID local enquanto um novo grupo está sendo carregado para evitar delay visual
  useEffect(() => {
    if (grupoSelecionado?.id) {
      setSelectedGrupoId(grupoSelecionado.id);
    } else if (!loadingGrupoSelecionado) {
      setSelectedGrupoId(undefined);
    }
  }, [grupoSelecionado?.id, loadingGrupoSelecionado]);

  const handleSelect = (grupoId: string) => {
    if (selectedGrupoId === grupoId) return;

    if (!loadingMensagensGrupo) {
      resetSelectedFiles();

      setSelectedGrupoId(grupoId);
      dispatch(fetchGrupoById(grupoId));
    }
  };

  const handleSearch = (value: string) => {
    setFilter(value);
    dispatch(filterGrupos(value));
  };

  const handleCancelSearch = () => {
    setFilter('');
    dispatch(filterGrupos(''));
  };

  const {
    contextMenuPosition,
    contextMenuItem: contextMenuGrupo,
    menuPaperRef,
    handleContextMenuOpen,
    handleContextMenuClose: handleClose,
  } = useCardContextMenu<GrupoInterface>();

  const handleMarcarComoNaoLido = (grupoId: string) => {
    dispatch(fetchMarcarGrupoComoNaoLido(grupoId))
      .unwrap()
      .then(() => {
        dispatch(setMarcarGrupoComoNaoLido(grupoId));
        enqueueSnackbar('Grupo marcado como não lido.', { variant: 'success' });
      })
      .catch(() => {
        enqueueSnackbar(
          'Não foi possível marcar como não lido. Tente novamente.',
          { variant: 'error' },
        );
      })
      .finally(() => {
        handleClose();
      });
  };

  const handleMarcarComoLido = (grupoId: string) => {
    dispatch(fetchMarcarGrupoComoLido(grupoId))
      .unwrap()
      .then(() => {
        dispatch(setMarcarGrupoComoLido(grupoId));
        enqueueSnackbar('Grupo marcado como lido.', { variant: 'success' });
      })
      .catch(() => {
        enqueueSnackbar('Não foi possível marcar como lido. Tente novamente.', {
          variant: 'error',
        });
      })
      .finally(() => {
        handleClose();
      });
  };
  const handleMarkAsRead = () => {
    confirm({
      title: 'Marcar todos os grupos como lidos',
      description:
        'Tem certeza de que deseja marcar todos os grupos como lidos?',
      dialogProps: { maxWidth: 'xs', fullWidth: true },
      cancellationText: 'Cancelar',
      confirmationText: 'Ok',
    })
      .then(() => {
        dispatch(fetchMarcarTodosGruposComoLidos())
          .unwrap()
          .then(() => {
            dispatch(setMarcarTodosGruposComoLidos());
            enqueueSnackbar('Todos os grupos foram marcados como lidos.', {
              variant: 'success',
            });
          })
          .catch(() => {
            enqueueSnackbar(
              'Não foi possível marcar todos os grupos como lidos. Tente novamente.',
            );
          });
      })
      .catch(() => undefined);
  };
  // event listeners
  useEffect(() => {
    // Observa quando um novo grupo é adicionado/criado
    const handlerAddNovoGrupo = (grupo: GrupoInterface) => {
      dispatch(addNovoGrupo(grupo));
    };
    // observa quando um grupo tem uma mudaça de status
    const handlerNovoStatus = (data: {
      grupoChatId: string;
      status: GrupoStatusType;
    }) => {
      dispatch(atualizarStatusGrupo(data));
    };
    // Observa se a foto de um grupo foi atualizada
    const handlerFotoAtualizada = (data: {
      grupoChatId: string;
      foto: string;
    }) => {
      dispatch(atualizarFotoGrupo(data));
    };

    const handleContarMensagem = (data: ContarNovaMensagemGrupoInterface) => {
      const { grupoChatId, totalNaoLidas: total, ultimaMensagemEm } = data;
      // atualiza o contador
      dispatch(
        setTotalMensagensNaoLidasGrupo({
          grupoChatId,
          total,
        }),
      );
      if (ultimaMensagemEm) {
        // atualiza a ultima mensagem recebida para manter a lista sempre ordenada
        dispatch(
          atualizarUltimaMensagemRecebidaGrupo({
            grupoChatId,
            ultimaMensagemEm,
          }),
        );
        if (total > 0) dispatch(reorderGrupos()); // reordena a lista
      }
    };
    // observa o evento separado de menção no canal do grupo
    const handleCanalMencionado = (data: {
      canalMencionado: boolean;
      grupoChatId: string;
    }) => {
      dispatch(setCanalMencionadoGrupo(data));
    };
    // observa quando do usuario aos acesso aos grupos foi alterado
    const handleAcessoGrupos = (mensagem: string) => {
      dispatch(setGrupoSelecionado());
      dispatch(fetchGrupos()).then(() => dispatch(reorderGrupos()));

      enqueueSnackbar(mensagem);
    };

    socket.on(socketEvents.ON_ADICIONADO_NOVO_GRUPO, handlerAddNovoGrupo);
    socket.on(socketEvents.ON_NOVO_STATUS_GRUPO, handlerNovoStatus);
    socket.on(socketEvents.ON_FOTO_GRUPO_ATUALIZADA, handlerFotoAtualizada);
    socket.on(socketEvents.ON_CONTAR_NOVA_MENSAGEM_GRUPO, handleContarMensagem);
    socket.on(socketEvents.ON_CANAL_MENCIONADO_GRUPO, handleCanalMencionado);
    socket.on(socketEvents.ON_ATUALIZACAO_ACESSO_GRUPOS, handleAcessoGrupos);

    return () => {
      socket.off(socketEvents.ON_ADICIONADO_NOVO_GRUPO, handlerAddNovoGrupo);
      socket.off(socketEvents.ON_NOVO_STATUS_GRUPO, handlerNovoStatus);
      socket.off(socketEvents.ON_FOTO_GRUPO_ATUALIZADA, handlerFotoAtualizada);
      socket.off(
        socketEvents.ON_CONTAR_NOVA_MENSAGEM_GRUPO,
        handleContarMensagem,
      );
      socket.off(socketEvents.ON_CANAL_MENCIONADO_GRUPO, handleCanalMencionado);
      socket.off(socketEvents.ON_ATUALIZACAO_ACESSO_GRUPOS, handleAcessoGrupos);
    };
  }, [dispatch, enqueueSnackbar]);

  // Carrega os grupos e ordena pela mensagem mais recente
  useEffect(() => {
    dispatch(fetchGrupos()).then(() => dispatch(reorderGrupos()));

    return () => {
      setFilter('');
      dispatch(resetGrupos());
      dispatch(setGrupoSelecionado());
    };
  }, [dispatch]);

  const parentRef = useRef<HTMLUListElement>(null);

  const items = loadingGrupos
    ? Array.from(new Array(4) as GrupoInterface[])
    : filteredGrupos;

  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 72,
    measureElement: el => el?.getBoundingClientRect().height,
    getItemKey: index => (loadingGrupos ? index : items[index]?.id ?? index),
    overscan: 5,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  const renderGrupos = virtualItems.map(virtualRow => {
    const grupo = items[virtualRow.index];
    const key = grupo ? grupo.id : virtualRow.index;

    if (grupo) {
      const {
        id,
        foto,
        nome,
        status,
        totalMensagensNaoLidas,
        canalMencionado,
      } = grupo;
      const selected = selectedGrupoId === id;
      const hasMarkedAsUnread = totalMensagensNaoLidas === -1;

      return (
        <div
          key={key}
          ref={rowVirtualizer.measureElement}
          data-index={virtualRow.index}
          onContextMenu={e => handleContextMenuOpen(e, grupo)}
        >
          <CardItem
            className={classes.cardItem}
            onSelect={() => handleSelect(grupo.id)}
            selected={selected}
          >
            <ListItem disableGutters dense>
              <ListItemAvatar style={{ minWidth: 0, marginRight: 8 }}>
                <Avatar src={foto} />
              </ListItemAvatar>

              <ListItemText
                primary={nome}
                secondary={
                  <>
                    {status === 'CRIADO' && (
                      <Typography noWrap variant="caption" color="primary">
                        Sincronizando grupo...
                      </Typography>
                    )}

                    {status === 'INATIVO' && (
                      <Typography noWrap variant="caption" color="error">
                        Você não faz mais parte do grupo!
                      </Typography>
                    )}
                  </>
                }
                primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
                secondaryTypographyProps={{ noWrap: true, variant: 'caption' }}
                style={{ margin: 0 }}
              />

              <Box ml={1} display="flex" alignItems="center" gridGap={4}>
                {canalMencionado && (
                  <AlternateEmailIcon color="error" style={{ fontSize: 18 }} />
                )}

                {totalMensagensNaoLidas > 0 && (
                  <Badge
                    color="error"
                    overlap="rectangular"
                    classes={{ badge: classes.badge }}
                    badgeContent={totalMensagensNaoLidas}
                  />
                )}

                {hasMarkedAsUnread && (
                  <Badge
                    color="error"
                    overlap="rectangular"
                    classes={{ badge: classes.badge }}
                    badgeContent=""
                  />
                )}
              </Box>
            </ListItem>
          </CardItem>
        </div>
      );
    }

    return (
      <div
        key={key}
        ref={rowVirtualizer.measureElement}
        data-index={virtualRow.index}
      >
        <CardItem disabled>
          <ListItem disableGutters dense>
            <ListItemAvatar style={{ minWidth: 0, marginRight: 8 }}>
              <Skeleton variant="circle">
                <Avatar />
              </Skeleton>
            </ListItemAvatar>
            <ListItemText
              primary={<Skeleton variant="rect" />}
              style={{ margin: 0 }}
            />
          </ListItem>
        </CardItem>
      </div>
    );
  });

  // so exibe a lista se ela nao estiver vazia
  if (!loadingGrupos && grupos.length <= 0) {
    return null;
  }

  return (
    <Grid
      item
      xs={4}
      sm={4}
      md={4}
      lg={3}
      component={Box}
      height="100%"
      overflow="hidden"
    >
      <Box className={classes.root}>
        <Box display="flex" alignItems="center" gridGap={8} mb={1}>
          <Box flexGrow={1}>
            <SearchBar
              value={filter}
              onChange={handleSearch}
              onCancelSearch={handleCancelSearch}
            />
          </Box>

          <FilterListaAtendimentos onMarkAllAsRead={handleMarkAsRead} />
        </Box>

        <List className={classes.list} disablePadding dense ref={parentRef}>
          <VirtualizedListContainer
            virtualItems={virtualItems}
            virtualizer={rowVirtualizer}
            loading={loadingGrupos}
            empty={
              filter
                ? {
                    title: 'Tem certeza que digitou certo?!',
                    description: `Não encontramos nenhum grupo com o nome '${filter}'.`,
                  }
                : undefined
            }
          >
            {renderGrupos}
          </VirtualizedListContainer>
        </List>

        <CardContextMenu
          position={contextMenuPosition}
          paperRef={menuPaperRef}
          hasUnread={
            (contextMenuGrupo?.totalMensagensNaoLidas ?? 0) > 0 ||
            contextMenuGrupo?.totalMensagensNaoLidas === -1
          }
          isMarkedAsUnread={contextMenuGrupo?.totalMensagensNaoLidas === -1}
          onMarkAsRead={() =>
            contextMenuGrupo && handleMarcarComoLido(contextMenuGrupo.id)
          }
          onMarkAsUnread={() =>
            contextMenuGrupo && handleMarcarComoNaoLido(contextMenuGrupo.id)
          }
        />
      </Box>
    </Grid>
  );
}
